<template>
  <v-card-text>
    <v-row>
      <v-col cols="12" md="3">
        <v-card rounded="lg" variant="outlined" class="pa-2">
          <div class="card-title-pro">
            <v-icon size="16" class="mr-1">mdi-tune</v-icon>
            {{ $t("opciones") }}
          </div>

          <v-divider class="mb-2" />
          <v-list nav density="comfortable">
            <v-list-item
              :active="mode === 'crear'"
              @click="mode = 'crear'"
              color="primary"
              rounded="lg"
            >
              <v-list-item-title>{{
                $t("crear_nuevo_equipo")
              }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              :active="mode === 'consultar'"
              @click="mode = 'consultar'"
              color="primary"
              rounded="lg"
            >
              <v-list-item-title>{{
                $t("consultar_equipos")
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" md="9">
        <div v-if="mode === 'crear'">
          <v-card rounded="lg" variant="outlined" class="pa-4 cool-card">
            <div class="text-h6 mb-4">{{ $t("crear_nuevo_equipo") }}</div>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="newTeamName"
                  :label="$t('nombre_equipo')"
                  density="comfortable"
                  hide-details
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="newTeamBossId"
                  :items="users"
                  item-title="label"
                  item-value="id"
                  :label="$t('lider2')"
                  density="comfortable"
                  hide-details
                  :loading="loadingUsers"
                  :disabled="loadingUsers || users.length === 0"
                  clearable
                />
              </v-col>
            </v-row>

            <div class="d-flex justify-end ga-2 mt-4">
              <v-btn
                color="success"
                variant="elevated"
                :disabled="!newTeamName"
                :loading="creatingTeam"
                @click="createTeam"
              >
                {{ $t("guardar") }}
              </v-btn>

              <v-btn variant="text" @click="mode = 'consultar'">{{
                $t("cerrar2")
              }}</v-btn>
            </div>
          </v-card>
        </div>

        <div v-else>
          <v-card
            rounded="lg"
            variant="outlined"
            class="pa-4 cool-card"
            :class="{ 'dark-table': isDark }"
          >
            <div
              class="d-flex align-center justify-space-between ga-2 mb-4 flex-wrap"
            >
              <div class="text-h6">{{ $t("Equipos") }}</div>

              <div class="flex-grow-1" style="max-width: 250px">
                <v-text-field
                  v-model.trim="search"
                  :label="$t('buscar')"
                  prepend-inner-icon="mdi-magnify"
                  density="comfortable"
                  hide-details
                  clearable
                  style="max-width: 600px"
                />
              </div>
            </div>

            <v-progress-linear
              v-if="loadingAll || loadingMap"
              indeterminate
              class="mb-3"
            />

            <v-alert
              v-if="!loadingAll && pagedRows.length === 0"
              type="info"
              variant="tonal"
            >
              {{ $t("buscar_input_tabEq") }}
            </v-alert>

            <v-table v-else density="comfortable">
              <thead>
                <tr>
                  <th class="text-left">{{ $t("Equipos") }}</th>
                  <th class="text-left">{{ $t("lider") }}</th>
                  <th class="text-left">Unidad</th>
                  <th class="text-left">{{ $t("proyecto_asignado") }}</th>
                  <th class="text-left">{{ $t("Acciones") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="t in pagedRows" :key="t.id">
                  <td>{{ t.teamname }}</td>
                  <td>{{ bossLabel(t) }}</td>
                   <td>{{t.unidad_nombre }}</td>
                  <td style="white-space: pre-line; font-weight: bold">
                    <v-chip
                      v-if="teamProjectLabel(t.id) == 'Sin Proyecto'"
                      class="ma-2"
                      :color="roleBadgeClass(teamProjectLabel(t.id))"
                    >
                      {{ teamProjectLabel(t.id) }} </v-chip
                    ><span v-else>
                      {{ teamProjectLabel(t.id) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <div class="actions actions--table">
                      <v-tooltip :text="$t('manage_members')" location="top">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            elevation="2"
                            class="action-btn action-btn--equipos"
                            @click="$emit('goMembers', t.id)"
                          >
                            <v-icon>mdi-account-group</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>

                      <v-tooltip :text="$t('edit_team')" location="top">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            elevation="2"
                            class="action-btn action-btn--equipos"
                            @click="openEdit(t)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>

                      <v-tooltip :text="$t('delete_team')" location="top">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            elevation="2"
                            class="action-btn action-btn--equipos"
                            @click="confirmDeleteTeam(t)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <v-divider class="my-4" />

            <div
              class="d-flex align-center justify-space-between flex-wrap ga-3"
            >
              <div class="d-flex align-center ga-2">
                <span class="text-caption text-medium-emphasis">{{
                  $t("paginacion")
                }}</span>
                <v-select
                  v-model="itemsPerPage"
                  :items="[5, 8, 10, 20]"
                  density="compact"
                  hide-details
                  style="max-width: 110px"
                />
              </div>

              <v-pagination
                v-model="page"
                :length="pageCount"
                density="comfortable"
                :total-visible="6"
              />
            </div>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <EditarEquipos
      v-model="dialogEditTeam"
      :team="EditTeam"
      :users="users"
      :loading-users="loadingUsers"
      @roles-updated="onTeamUpdated"
    />
  </v-card-text>
</template>

<script>
import { swal } from "@/utils/swal.js";
import { useSettingsStore } from "@/stores/settings";
import "@/css/Global.css";
import EditarEquipos from "./EditarEquipos.vue";
import {
  getAllTeams,
  getLeaderUsers,
  getProyectos,
  createTeam,
  getTeamsByProyecto,
  deleteTeam as deleteTeamSvc,
} from "@/api/equipos.js";
export default {
  name: "TabEquipos",
  components: { EditarEquipos },
  props: {
    apiBase: { type: String, required: true },
    toast: { type: Function, required: true },
  },
  emits: ["goMembers"],

  data() {
    return {
      mode: "consultar",

      search: "",
      page: 1,
      itemsPerPage: 8,
      EditTeam: null,
      dialogEditTeam: false,

      loadingAll: false,
      loadingUsers: false,
      loadingMap: false,

      allTeams: [],
      users: [],

      newTeamName: "",
      newTeamBossId: null,
      creatingTeam: false,

      teamToProjectMap: {},
    };
  },

  computed: {
    reactiveTeamToProjectMap() {
      // simplemente devuelve el map

      return this.teamToProjectMap;
    },

    isDark() {
      const settings = useSettingsStore();
      return settings.theme === "dark";
    },
    filteredTeams() {
      const q = (this.search || "").toLowerCase();
      if (!q) return this.allTeams;

      return this.allTeams.filter((t) => {
        const name = (t.teamname || "").toLowerCase();
        const nameunidad = (t.unidad_nombre || "").toLowerCase();
        const boss = (this.bossLabel(t) || "").toLowerCase();
        const proj = (this.teamProjectLabel(t.id) || "").toLowerCase();
        return nameunidad.includes(q) || name.includes(q) || boss.includes(q) || proj.includes(q);
      });
    },

    pageCount() {
      const total = this.filteredTeams.length;
      const per = Number(this.itemsPerPage) || 8;
      return Math.max(1, Math.ceil(total / per));
    },

    pagedRows() {
      const per = Number(this.itemsPerPage) || 8;
      const p = Math.min(Math.max(this.page, 1), this.pageCount);
      const start = (p - 1) * per;
      return this.filteredTeams.slice(start, start + per);
    },
  },

  watch: {
    mode: {
      immediate: true,
      async handler(v) {
        if (v === "consultar") {
          await this.refreshConsult();
        } else {
          if (this.users.length === 0) await this.fetchUsers();
        }
      },
    },
    teamToProjectMap: {
      deep: true,
      handler() {
        // Forzar recalculo de la tabla

        this.$forceUpdate();
      },
    },

    itemsPerPage() {
      this.page = 1;
    },

    search() {
      this.page = 1;
    },

    pageCount() {
      if (this.page > this.pageCount) this.page = this.pageCount;
    },
  },

  async mounted() {
    await Promise.all([this.fetchUsers(), this.refreshConsult()]);
  },

  methods: {
    roleBadgeClass(role) {
      //console.log("44477", role);
      switch (role) {
        case "Sin Proyecto":
          return "warning";
        default:
          return "secondary";
      }
    },
    swalCfg(icon, title, text) {
      return {
        icon,
        title,
        text,
        timer: 2200,
        showConfirmButton: false,
        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
        },
      };
    },

    async confirmDeleteTeam(team) {
      const ok = await swal({
        icon: "warning",
        title: this.$t("delete_team_question"),
        text: this.$t("delete_team_confirm", { name: team?.teamname }),
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: this.$t("yes_delete"),
        cancelButtonText: this.$t("cancel"),
        iconColor: "#DC2626",
        confirmButtonColor: "#DC2626",
        cancelButtonColor: "#6B7280",
        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
          confirmButton: "swal-btn-danger",
          cancelButton: "swal-btn-cancel",
        },
      });

      if (!ok.isConfirmed) return;
      await this.deleteTeam(team?.id, team?.teamname);
    },

    async deleteTeam(teamId, teamName) {
      if (!teamId) return;

      try {
        const resp = await deleteTeamSvc(teamId);

        if (resp.status === 204) {
          await swal({
            icon: "success",
            title: this.$t("team_deleted"),
            text: this.$t("team_deleted_ok", { name: teamName }),
            iconColor: "#16A34A",
            confirmButtonColor: "#16A34A",
            timer: 2000,
            showConfirmButton: false,
            customClass: {
              popup: this.isDark ? "mi-popup" : "mi-popup-2",
              title: "mi-titulo",
            },
          });

          await this.refreshConsult();
          return;
        }
      } catch (err) {
       await swal({
          icon: "warning",
          title: this.$t("cannot_delete"),
          text: err.response?.data?.error || this.$t("team_operation_error"),
          iconColor: "#F59E0B",
          confirmButtonColor: "#2563EB",
          customClass: {
            popup: this.isDark ? "mi-popup" : "mi-popup-2",
            title: "mi-titulo",
          },
        });
      }
    },
    async onTeamUpdated() {
      this.dialogEditTeam = false;
      await this.refreshConsult();
    },
    openEdit(t) {
      this.EditTeam = t;
      this.dialogEditTeam = true;
    },
    bossLabel(team) {
      return (
        team?.boss?.username ||
        team?.boss_username ||
        team?.boss_name ||
        team?.boss?.email ||
        this.$t("without_leader")
      );
    },

    teamProjectLabel(teamId) {
      const projects = this.teamToProjectMap?.[teamId];
       console.log("ESSSS", projects,teamId)
      if (!projects?.length) return this.$t("without_project");
     
      return projects.map((p) => p.idProyect).join("\n");
    },

    projectKey(p) {
      return p?.clave || p?.idProyect || p?.id || null;
    },

    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const raw = await getLeaderUsers();
        this.users = (raw || []).map((u) => ({
          id: u.id,
          label: `${u.username || "User " + u.id}${
            u.email ? " · " + u.email : ""
          }`,
        }));
      } catch (e) {
        this.users = [];
      } finally {
        this.loadingUsers = false;
      }
    },

    async fetchAllTeams() {
      this.loadingAll = true;
      try {
        this.allTeams = await getAllTeams();
      } catch (e) {
        this.allTeams = [];
      } finally {
        this.loadingAll = false;
      }
    },

    async buildTeamToProjectMap() {
      this.loadingMap = true;
      try {
        const proyectos = await getProyectos();
        const map = {};
        console.log("Proyectoss", proyectos)
        await Promise.all(
          (proyectos || []).map(async (p) => {
            const clave = this.projectKey(p);
            if (!clave) return;

            try {
              const assigned = await getTeamsByProyecto(clave);

              for (const t of assigned || []) {
                const teamId = t?.id || t?.team_id || t?.team?.id;
                if (!teamId) continue;

                if (!map[teamId]) map[teamId] = [];

                map[teamId].push({
                  clave,
                  idProyect: clave,
                  nameProyect: p?.nameProyect,
                  nombre: p?.nombre,
                });
              }
            } catch (e) {
              return;
            }
          }),
        );

        this.teamToProjectMap = { ...map };
      } catch (e) {
        this.teamToProjectMap = {};
      } finally {
        this.loadingMap = false;
      }
    },

    async refreshConsult() {
      await this.fetchAllTeams();
      await this.buildTeamToProjectMap();
    },

    async createTeam() {
      const name = (this.newTeamName || "").trim();
      if (!name) return;

      this.creatingTeam = true;
      try {
        const payload = { teamname: name };
        if (this.newTeamBossId) payload.boss_id = this.newTeamBossId;
        await createTeam(payload);

        this.newTeamName = "";
        this.newTeamBossId = null;

        this.mode = "consultar";
        await this.refreshConsult();

        await swal({
          icon: "success",
          title: this.$t("team"),
          text: this.$t("team_created_ok"),
          timer: 2500,
          showConfirmButton: false,
          customClass: {
            popup: this.isDark ? "mi-popup" : "mi-popup-2",
            title: "mi-titulo",
          },
        });
      } catch (err) {
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          this.$t("team_create_duplicate");
       await swal({
          icon: "error",
          title: this.$t("error"),
          text: msg,
          timer: 2500,
          showConfirmButton: false,
          customClass: {
            popup: this.isDark ? "mi-popup" : "mi-popup-2",
            title: "mi-titulo",
          },
        });
      } finally {
        this.creatingTeam = false;
      }
    },
  },
};
</script>
